import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Plus } from "lucide-react";

export default function Organization() {
  const { user } = useAuth();
  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", role: "learner" });
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    const o = await api.get("/organizations/mine");
    setOrg(o.data);
    if (o.data?.org_id) {
      const m = await api.get(`/organizations/${o.data.org_id}/members`);
      setMembers(m.data);
    }
  };

  useEffect(() => { refresh(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post(`/organizations/${org.org_id}/invite`, form);
      toast.success(`Added ${form.email}`);
      if (r.data.temp_password) toast.info(`Temp password: ${r.data.temp_password}`);
      setForm({ name: "", email: "", role: "learner" });
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invite failed");
    } finally {
      setLoading(false);
    }
  };

  if (!org) return <Layout><div className="px-10 py-20">No organization yet.</div></Layout>;

  return (
    <Layout>
      <div className="px-10 py-10 max-w-5xl">
        <div className="overline mb-3">Tenant</div>
        <h1 className="editorial-h1 text-5xl mb-2">{org.name}</h1>
        <div className="text-[#52525B] mb-10">{members.length} members · {org.plan} plan</div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-7">
            <h2 className="editorial-h2 text-3xl mb-4">Members</h2>
            <div className="editorial-card-static">
              {members.map((m, i) => (
                <div key={m.user_id} className={`px-5 py-4 flex items-center justify-between ${i > 0 ? "border-t border-[#E4E4E7]" : ""}`} data-testid={`member-${m.user_id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#0A0A0A] text-white flex items-center justify-center font-display">{m.name?.[0]?.toUpperCase()}</div>
                    <div>
                      <div className="font-display">{m.name}</div>
                      <div className="overline">{m.email}</div>
                    </div>
                  </div>
                  <div className="overline">{m.role?.replace("_", " ")}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-5">
            <h2 className="editorial-h2 text-3xl mb-4">Invite</h2>
            <form onSubmit={invite} className="editorial-card-static p-6 space-y-4">
              <div>
                <label className="overline block mb-2">Name</label>
                <input data-testid="invite-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7]" />
              </div>
              <div>
                <label className="overline block mb-2">Email</label>
                <input data-testid="invite-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7]" />
              </div>
              <div>
                <label className="overline block mb-2">Role</label>
                <select data-testid="invite-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] bg-white">
                  <option value="learner">Learner</option>
                  <option value="instructor">Instructor</option>
                </select>
              </div>
              <button type="submit" data-testid="invite-submit" disabled={loading} className="btn-primary w-full justify-center"><Plus size={14}/> {loading ? "Sending…" : "Add member"}</button>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
}
